import { useEffect, useState } from "react";
import { 
  CalendarDays, 
  LoaderCircle, 
  Eye, 
  CheckCircle2, 
  AlertTriangle, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  ShieldCheck,
  TrendingUp,
  CreditCard,
  Users
} from "lucide-react";
import { useSelectedEvent } from "../contexts/SelectedEventContext";
import { useNotifications } from "../contexts/NotificationContext";
import eventService from "../services/eventService";
import "../styles/pages/Pagos.css";

function ConfigEvento() {
	const { eventos, cargarEventos } = useSelectedEvent();
	const { showError, showSuccess } = useNotifications();
	
	const [eventoId, setEventoId] = useState("");
	const [costo, setCosto] = useState("");
	const [fechas, setFechas] = useState([]);
	const [porcentajes, setPorcentajes] = useState([]);
	
	const [loading, setLoading] = useState(false);
	const [cargandoPreview, setCargandoPreview] = useState(false);
	const [ejecutando, setEjecutando] = useState(false);
	
	const [modalPreviewAbierto, setModalPreviewAbierto] = useState(false);
	const [datosPreview, setDatosPreview] = useState(null);
	const [paginaPreview, setPaginaPreview] = useState(1);
	const [cargandoPagina, setCargandoPagina] = useState(false);

	const porcentajesPorDefecto = (cantidad) => {
		if (cantidad === 1) return [100];
		if (cantidad === 2) return [50, 100];
		if (cantidad === 3) return [50, 75, 100];

		return Array.from({ length: cantidad }, (_, index) =>
			Math.round(((index + 1) / cantidad) * 100)
		);
	};

	const obtenerPorcentajesDeEvento = (evento, cantidadFechas) => {
		if (
			evento?.configuracion_pago?.activo &&
			Array.isArray(evento.configuracion_pago.reglas) &&
			evento.configuracion_pago.reglas.length === cantidadFechas
		) {
			return evento.configuracion_pago.reglas.map((r) => r.porcentaje_acumulado);
		}

		if (Array.isArray(evento?.porcentajes_pago) && evento.porcentajes_pago.length === cantidadFechas) {
			return evento.porcentajes_pago;
		}

		return porcentajesPorDefecto(cantidadFechas);
	};

	useEffect(() => {
		if (!eventos || eventos.length === 0) {
			setLoading(true);
			cargarEventos().finally(() => setLoading(false));
		}
	}, [eventos, cargarEventos]);

	const eventoSeleccionado = eventos?.find(
		(evento) => (evento.id || evento._id) === eventoId
	);

	const seleccionarEvento = (id) => {
		setEventoId(id);
		const evento = eventos?.find((item) => (item.id || item._id) === id);
		if (!evento) return;

		const fechasEvento = evento.fechas_facturas || evento.fechas || [];
		setCosto(evento.costo ?? "");
		const fechasValidas = fechasEvento.filter(Boolean);
		setFechas(fechasValidas);
		setPorcentajes(obtenerPorcentajesDeEvento(evento, fechasValidas.length));
	};

	const cambiarFecha = (index, valor) => {
		setFechas((actuales) => actuales.map((fecha, i) => (i === index ? valor : fecha)));
	};

	const agregarFecha = () => {
		setFechas((actuales) => [...actuales, ""]);
		setPorcentajes((actuales) => {
			const ultimo = actuales.at(-1) || 100;
			return [...actuales, ultimo];
		});
	};

	const eliminarFecha = (index) => {
		setFechas((actuales) => actuales.filter((_, i) => i !== index));
		setPorcentajes((actuales) => actuales.filter((_, i) => i !== index));
	};

	const cambiarPorcentaje = (index, valor) => {
		setPorcentajes((actuales) => actuales.map((porcentaje, i) => (
			i === index ? Number(valor) : porcentaje
		)));
	};

	const obtenerMonto = (porcentaje) => (Number(costo || 0) * Number(porcentaje || 0)) / 100;

	const obtenerPagoAdicional = (index) => {
		const porcentajeAnterior = index === 0 ? 0 : porcentajes[index - 1] || 0;
		return obtenerMonto((porcentajes[index] || 0) - porcentajeAnterior);
	};

	const construirReglas = () => {
		return fechas.map((fecha, idx) => ({
			orden: idx + 1,
			fecha: fecha,
			porcentaje_acumulado: Number(porcentajes[idx])
		}));
	};

	const validarFormularioLocal = () => {
		if (!costo || Number(costo) <= 0) {
			showError("El precio por boleto debe ser mayor a 0");
			return false;
		}
		if (fechas.length === 0 || fechas.some((f) => !f)) {
			showError("Debe ingresar todas las fechas requeridas");
			return false;
		}
		// Validar orden cronológico
		for (let i = 1; i < fechas.length; i++) {
			if (fechas[i] <= fechas[i - 1]) {
				showError(`Las fechas deben estar en orden cronológico estricto. Fecha #${i + 1} no es posterior a Fecha #${i}`);
				return false;
			}
		}
		// Validar porcentajes
		if (porcentajes[0] <= 0) {
			showError("El primer porcentaje acumulado debe ser mayor a 0%");
			return false;
		}
		for (let i = 1; i < porcentajes.length; i++) {
			if (porcentajes[i] < porcentajes[i - 1]) {
				showError(`Los porcentajes acumulados deben ser monótonamente crecientes (Pago #${i + 1} es menor al anterior)`);
				return false;
			}
		}
		if (porcentajes.at(-1) !== 100) {
			showError("El último porcentaje acumulado debe ser exactamente 100%");
			return false;
		}

		return true;
	};

	const solicitarPreview = async (pagina = 1) => {
		if (!eventoSeleccionado) {
			showError("Selecciona un evento primero");
			return;
		}
		if (!validarFormularioLocal()) return;

		setCargandoPreview(true);
		if (pagina > 1) setCargandoPagina(true);

		const id = eventoSeleccionado.id || eventoSeleccionado._id;
		const reglas = construirReglas();

		const res = await eventService.previsualizarModificacionExcepcional(
			id,
			{ nuevo_costo: Number(costo), reglas },
			{ pagina, limit: 8 }
		);

		setCargandoPreview(false);
		setCargandoPagina(false);

		if (res.success && res.data) {
			setDatosPreview(res.data);
			setPaginaPreview(pagina);
			setModalPreviewAbierto(true);
		} else {
			if (res.code === "NO_HAY_CAMBIOS_PARA_APLICAR") {
				showError("No se detectaron cambios en el precio ni en las fechas de este evento.");
			} else {
				showError(res.error || "No se pudo generar la previsualización.");
			}
		}
	};

	const ejecutarModificacion = async () => {
		if (!datosPreview || !eventoSeleccionado) return;

		setEjecutando(true);
		const id = eventoSeleccionado.id || eventoSeleccionado._id;
		const reglas = construirReglas();

		const res = await eventService.ejecutarModificacionExcepcional(id, {
			nuevo_costo: Number(costo),
			reglas,
			preview_token: datosPreview.preview_token,
			modificacion_id: datosPreview.modificacion_id
		});

		setEjecutando(false);

		if (res.success) {
			showSuccess("¡Modificación excepcional aplicada exitosamente!");
			setModalPreviewAbierto(false);
			setDatosPreview(null);
			cargarEventos();
		} else {
			showError(res.error || "Error al ejecutar la modificación excepcional");
		}
	};

	return (
		<main className="bg-white dark:bg-[#1e1e1e] rounded-3xl p-6 md:p-8 w-full shadow-sm">
			<div className="mb-6 border-b border-gray-100 dark:border-gray-800 pb-5">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
							<ShieldCheck className="w-6 h-6 text-[#246370]" />
							Modificación Excepcional de Evento
						</h2>
						<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
							Permite ajustar precios, calendario de fechas y porcentajes acumulados con reconciliación segura de facturas y Toku.
						</p>
					</div>
				</div>
			</div>

			<form onSubmit={(e) => { e.preventDefault(); solicitarPreview(1); }} className="space-y-6 max-w-3xl">
				<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
					Evento
					<select
						value={eventoId}
						onChange={(event) => seleccionarEvento(event.target.value)}
						disabled={loading}
						className="mt-2 w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-[#1e1e1e] text-gray-800 dark:text-gray-100"
					>
						<option value="">{loading ? "Cargando eventos..." : "-- Seleccionar evento --"}</option>
						{(eventos || []).map((evento) => (
							<option key={evento.id || evento._id} value={evento.id || evento._id}>
								{evento.nombre_evento || evento.nombre || evento.id} {evento.costo ? `($${evento.costo})` : ""}
							</option>
						))}
					</select>
				</label>

				{eventoSeleccionado && (
					<>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
								Precio actual en sistema
								<input
									type="text"
									disabled
									value={`$${eventoSeleccionado.costo || 0} MXN`}
									className="mt-2 w-full h-11 px-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 dark:bg-[#2a2a2a] dark:border-gray-700 dark:text-gray-400"
								/>
							</label>

							<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
								Nuevo precio por boleto
								<input
									type="number"
									min="1"
									step="0.01"
									onWheel={(e) => e.target.blur()}
									onKeyDown={(e) => {
										if (e.key === '-' || e.key === 'e' || e.key === 'E') {
											e.preventDefault();
										}
									}}
									value={costo}
									onChange={(event) => setCosto(event.target.value)}
									className="mt-2 w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-[#1e1e1e] text-gray-800 dark:text-gray-100 font-bold text-lg text-[#246370] hide-arrows"
									placeholder="Ej. 2230"
								/>
							</label>
						</div>

						<section className="mt-4">
							<div className="flex items-center justify-between mb-3">
								<h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
									<CalendarDays className="w-4 h-4 text-[#246370]" /> Calendario de Pagos y Metas Porcentuales
								</h3>
								<button type="button" onClick={agregarFecha} className="text-sm font-semibold text-[#246370] hover:underline">
									+ Agregar fecha
								</button>
							</div>
							
							<div className="space-y-3">
								{fechas.map((fecha, index) => {
									const porcentajeAnterior = index === 0 ? 0 : porcentajes[index - 1] || 0;
									const porcentajeCuota = (porcentajes[index] || 0) - porcentajeAnterior;
									const esCuotaCero = porcentajeCuota === 0;

									return (
										<div key={`${fecha}-${index}`} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3 bg-gray-50/50 dark:bg-[#252525]">
											<div className="flex flex-col md:flex-row md:items-end gap-3">
												<label className="flex-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
													Cuota #{index + 1} - Fecha límite
													<input 
														type="date" 
														value={fecha} 
														onChange={(event) => cambiarFecha(index, event.target.value)} 
														className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-[#1e1e1e] text-gray-800 dark:text-gray-100" 
													/>
												</label>
												
												<label className="w-full md:w-44 text-xs font-semibold text-gray-500 dark:text-gray-400">
													Meta acumulada
													<div className="relative mt-1">
														<input 
															type="number" 
															min="1" 
															max="100" 
															step="1" 
															onWheel={(e) => e.target.blur()}
															onKeyDown={(e) => {
																if (e.key === '-' || e.key === 'e' || e.key === 'E') {
																	e.preventDefault();
																}
															}}
															value={porcentajes[index] ?? ""} 
															onChange={(event) => cambiarPorcentaje(index, event.target.value)} 
															className="w-full h-10 px-3 pr-8 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-[#1e1e1e] text-gray-800 dark:text-gray-100 font-semibold hide-arrows" 
														/>
														<span className="absolute right-3 top-2.5 text-gray-400 text-sm">%</span>
													</div>
												</label>

												{fechas.length > 1 && (
													<button 
														type="button" 
														onClick={() => eliminarFecha(index)} 
														className="h-10 px-3 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium" 
														aria-label="Eliminar fecha"
													>
														Eliminar
													</button>
												)}
											</div>

											<div className="flex flex-wrap items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-700">
												<div className="flex gap-4">
													<span>Meta acumulada: <strong className="text-[#246370]">${obtenerMonto(porcentajes[index]).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</strong></span>
													<span>Cuota requerida: <strong className="text-gray-700 dark:text-gray-200">${obtenerPagoAdicional(index).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</strong> ({porcentajeCuota}%)</span>
												</div>
												{esCuotaCero && (
													<span className="text-amber-600 dark:text-amber-400 font-medium">⚠️ Meta igual a la anterior (cuota $0.00)</span>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</section>

						<div className="flex justify-end pt-4">
							<button 
								type="submit" 
								disabled={cargandoPreview} 
								className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-[#246370] hover:bg-[#1a4f5a] disabled:opacity-50 text-white font-semibold shadow-sm transition-all"
							>
								{cargandoPreview ? <LoaderCircle className="w-5 h-5 animate-spin" /> : <Eye className="w-5 h-5" />}
								{cargandoPreview ? "Calculando impacto..." : "Previsualizar impacto financiero"}
							</button>
						</div>
					</>
				)}
			</form>

			{/* MODAL DE PREVISUALIZACIÓN ENRIQUECIDA */}
			{modalPreviewAbierto && datosPreview && (
				<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
					<div className="bg-white dark:bg-[#1e1e1e] rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl border border-gray-100 dark:border-gray-700">
						
						{/* Header Modal */}
						<div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
							<div>
								<span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
									Tipo: {datosPreview.tipo_modificacion}
								</span>
								<h3 className="text-xl font-bold text-gray-900 dark:text-white mt-1">
									Resumen de Impacto Financiero
								</h3>
								<p className="text-xs text-gray-500 dark:text-gray-400">
									Evento: {datosPreview.resumen_evento.nombre_evento} | ID Modificación: {datosPreview.modificacion_id}
								</p>
							</div>
							<button 
								onClick={() => setModalPreviewAbierto(false)} 
								className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
							>
								<X className="w-5 h-5" />
							</button>
						</div>

						{/* Body Modal Scrollable */}
						<div className="p-6 space-y-6 overflow-y-auto flex-1">
							
							{/* Advertencias si existen */}
							{datosPreview.advertencias?.length > 0 && (
								<div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800">
									<h4 className="text-sm font-bold text-amber-800 dark:text-amber-300 flex items-center gap-2">
										<AlertTriangle className="w-4 h-4" /> Advertencias del Sistema
									</h4>
									<ul className="mt-2 text-xs text-amber-700 dark:text-amber-400 list-disc list-inside space-y-1">
										{datosPreview.advertencias.map((adv, i) => (
											<li key={i}>{adv}</li>
										))}
									</ul>
								</div>
							)}

							{/* Tarjetas de Métricas Globales */}
							<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
								<div className="p-4 rounded-xl bg-gray-50 dark:bg-[#252525] border border-gray-100 dark:border-gray-800">
									<span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
										<Users className="w-3.5 h-3.5" /> Invitados Afectados
									</span>
									<p className="text-xl font-bold text-gray-800 dark:text-white mt-1">
										{datosPreview.impacto_financiero.total_invitados_afectados}
									</p>
								</div>

								<div className="p-4 rounded-xl bg-gray-50 dark:bg-[#252525] border border-gray-100 dark:border-gray-800">
									<span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
										<TrendingUp className="w-3.5 h-3.5" /> Total Obligación
									</span>
									<p className="text-xl font-bold text-gray-800 dark:text-white mt-1">
										${datosPreview.impacto_financiero.monto_total_nuevo?.toLocaleString("es-MX")}
									</p>
									<span className="text-[10px] text-gray-400">
										Anterior: ${datosPreview.impacto_financiero.monto_total_anterior?.toLocaleString("es-MX")}
									</span>
								</div>

								<div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-800/40">
									<span className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
										<ShieldCheck className="w-3.5 h-3.5" /> Pagado Intacto
									</span>
									<p className="text-xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">
										${datosPreview.impacto_financiero.total_pagado_historico?.toLocaleString("es-MX")}
									</p>
									<span className="text-[10px] text-emerald-600/80 dark:text-emerald-400/80">
										100% inmutable
									</span>
								</div>

								<div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-800/40">
									<span className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-1">
										<CreditCard className="w-3.5 h-3.5" /> Saldo a Cobrar
									</span>
									<p className="text-xl font-bold text-blue-700 dark:text-blue-300 mt-1">
										${datosPreview.impacto_financiero.saldo_pendiente_global_nuevo?.toLocaleString("es-MX")}
									</p>
								</div>
							</div>

							{/* Operaciones que NO se realizarán (Garantía de Seguridad) */}
							<div className="p-4 rounded-xl bg-gray-50 dark:bg-[#252525] border border-gray-200 dark:border-gray-700">
								<h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-2">
									<ShieldCheck className="w-4 h-4 text-emerald-600" /> Operaciones que NO se realizarán (Seguridad)
								</h4>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-600 dark:text-gray-400">
									<div>• <strong>{datosPreview.operaciones_que_no_se_realizaran?.pagos_historicos_no_modificados || 0}</strong> pagos históricos: NO modificados</div>
									<div>• <strong>{datosPreview.operaciones_que_no_se_realizaran?.facturas_pagadas_intactas || 0}</strong> facturas pagadas: Intactas</div>
									<div>• <strong>0</strong> devoluciones automáticas (gestión administrativa)</div>
								</div>
							</div>

							{/* Desglose Individual Paginado */}
							<div>
								<div className="flex items-center justify-between mb-2">
									<h4 className="text-sm font-bold text-gray-800 dark:text-white">
										Desglose por Invitado (Muestra)
									</h4>
									<span className="text-xs text-gray-400">
										Página {datosPreview.desglose_invitados_paginado?.pagina} de {datosPreview.desglose_invitados_paginado?.total_paginas}
									</span>
								</div>

								<div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
									<table className="w-full text-left text-xs">
										<thead className="bg-gray-100 dark:bg-[#2a2a2a] text-gray-600 dark:text-gray-300 font-semibold">
											<tr>
												<th className="p-2.5">Invitado</th>
												<th className="p-2.5">Boletos</th>
												<th className="p-2.5">Total Anterior</th>
												<th className="p-2.5">Nuevo Total</th>
												<th className="p-2.5">Pagado</th>
												<th className="p-2.5">Nuevo Saldo</th>
												<th className="p-2.5">Estado</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-gray-100 dark:divide-gray-800">
											{datosPreview.desglose_invitados_paginado?.items?.map((inv) => (
												<tr key={inv.id_invitado} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50">
													<td className="p-2.5 font-medium text-gray-800 dark:text-gray-200">{inv.nombre}</td>
													<td className="p-2.5 text-gray-600 dark:text-gray-400">{inv.boletos}</td>
													<td className="p-2.5 text-gray-500">${inv.total_anterior?.toLocaleString("es-MX")}</td>
													<td className="p-2.5 font-semibold text-gray-800 dark:text-white">${inv.total_nuevo?.toLocaleString("es-MX")}</td>
													<td className="p-2.5 text-emerald-600 font-medium">${inv.pagado_historico?.toLocaleString("es-MX")}</td>
													<td className="p-2.5 font-bold text-[#246370]">${inv.saldo_nuevo?.toLocaleString("es-MX")}</td>
													<td className="p-2.5">
														<span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
															inv.estado_resultante === "PAGADA" 
																? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
																: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
														}`}>
															{inv.estado_resultante}
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								{/* Paginador */}
								{datosPreview.desglose_invitados_paginado?.total_paginas > 1 && (
									<div className="flex items-center justify-between mt-3 text-xs">
										<button
											type="button"
											disabled={paginaPreview <= 1 || cargandoPagina}
											onClick={() => solicitarPreview(paginaPreview - 1)}
											className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40"
										>
											<ChevronLeft className="w-3.5 h-3.5" /> Anterior
										</button>
										<span>{paginaPreview} de {datosPreview.desglose_invitados_paginado.total_paginas}</span>
										<button
											type="button"
											disabled={paginaPreview >= datosPreview.desglose_invitados_paginado.total_paginas || cargandoPagina}
											onClick={() => solicitarPreview(paginaPreview + 1)}
											className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40"
										>
											Siguiente <ChevronRight className="w-3.5 h-3.5" />
										</button>
									</div>
								)}
							</div>
						</div>

						{/* Footer Modal */}
						<div className="p-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-[#252525]">
							<span className="text-xs text-gray-500 dark:text-gray-400">
								Token válido por 15 min. Protegido contra colisiones concurrentes.
							</span>
							<div className="flex gap-3">
								<button
									type="button"
									onClick={() => setModalPreviewAbierto(false)}
									disabled={ejecutando}
									className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold text-sm hover:bg-gray-100"
								>
									Cancelar
								</button>
								<button
									type="button"
									onClick={ejecutarModificacion}
									disabled={ejecutando}
									className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm disabled:opacity-50"
								>
									{ejecutando ? <LoaderCircle className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
									{ejecutando ? "Ejecutando y sincronizando..." : "Confirmar y Aplicar Modificación"}
								</button>
							</div>
						</div>

					</div>
				</div>
			)}
		</main>
	);
}

export default ConfigEvento;